import type { TeamPermission } from "@facturar/auth";
/**
 * Typed wrappers around the `/api/me/teams/*` BFF surface. Mirrors the Zod
 * schemas declared in `apps/api/src/routes/teams.ts` so request/response
 * shapes never drift.
 *
 * Every wrapper goes through `fetchApiWithAuth`, which automatically attaches
 * the Bearer token and the `X-Team-Id` header (when one is set in the
 * TeamProvider) for `/api/me/companies` + `/api/me/dian` paths. Team-scoped
 * routes here do NOT need that header — the path itself carries the teamId.
 */
import { fetchApiPublic, fetchApiWithAuth } from "@/lib/api";

export type TeamSummary = {
  customRoleId?: string | null;
  customRoleName?: string | null;
  permissions?: TeamPermission[];
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  role: string;
  joinedAt: string;
};

export type TeamMember = {
  customRoleId?: string | null;
  customRoleName?: string | null;
  permissions?: TeamPermission[];
  userId: string;
  displayName: string | null;
  phone: string;
  role: string;
  joinedAt: string;
};

export type TeamDetail = {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  createdAt: string;
  updatedAt: string;
  members: TeamMember[];
  pendingInvitationsCount: number;
};

export type TeamCreated = {
  id: string;
  name: string;
  slug: string;
  role: "owner";
  createdAt: string;
};

export type Invitation = {
  customRoleId?: string;
  customRoleName?: string;
  id: string;
  phone: string;
  role: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  acceptedUserId: string | null;
  acceptedAt: string | null;
};

export type InvitationCreated = {
  invitationId: string;
  phone: string;
  role: string;
  expiresAt: string;
  sentAt: string;
};

export type InvitationCancelled = {
  id: string;
  status: "cancelled";
};

export type TeamJoined = {
  teamId: string;
  teamName: string;
  role: string;
  joinedAt: string;
};

/** Sanitized public view behind a WhatsApp URL-button token (PF-345). */
export type InvitationTokenLookup = {
  kind: "accept" | "decline";
  invitationId: string;
  teamId: string;
  teamName: string;
  role: string;
  /** 'pending' | 'accepted' | 'cancelled' | 'declined' | 'expired' (effective). */
  status: string;
  expiresAt: string;
  invitedPhoneMasked: string;
  inviterName: string | null;
};

export type InvitationDeclined = {
  invitationId: string;
  teamName: string;
  status: "declined";
};

export type LeaveTeamResponse = {
  teamId: string;
  userId: string;
  status: "removed";
};

export type CreateTeamInput = {
  name: string;
  slug?: string;
};

export type UpdateTeamInput = {
  name?: string;
  slug?: string;
};

/** Shape returned by PATCH /:teamId and the archive/reactivate endpoints. */
export type TeamUpdated = {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  createdAt: string;
  updatedAt: string;
};

export type CreateInvitationInput = {
  phone: string;
  role?: "admin" | "accountant" | "cashier" | "viewer";
  customRoleId?: string;
};

// ── Wrappers ──────────────────────────────────────────────────────────────

export function listTeams(): Promise<TeamSummary[]> {
  return fetchApiWithAuth<TeamSummary[]>("/api/me/teams");
}

export function createTeam(input: CreateTeamInput): Promise<TeamCreated> {
  return fetchApiWithAuth<TeamCreated>("/api/me/teams", {
    method: "POST",
    body: input,
  });
}

export function getTeam(teamId: string): Promise<TeamDetail> {
  return fetchApiWithAuth<TeamDetail>(
    `/api/me/teams/${encodeURIComponent(teamId)}`,
  );
}

export function updateTeam(
  teamId: string,
  input: UpdateTeamInput,
): Promise<TeamUpdated> {
  return fetchApiWithAuth<TeamUpdated>(
    `/api/me/teams/${encodeURIComponent(teamId)}`,
    { method: "PATCH", body: input },
  );
}

/** Tenant shutdown — every company in the team must be archived first. */
export function archiveTeam(teamId: string): Promise<TeamUpdated> {
  return fetchApiWithAuth<TeamUpdated>(
    `/api/me/teams/${encodeURIComponent(teamId)}/archive`,
    { method: "POST" },
  );
}

export function reactivateTeam(teamId: string): Promise<TeamUpdated> {
  return fetchApiWithAuth<TeamUpdated>(
    `/api/me/teams/${encodeURIComponent(teamId)}/reactivate`,
    { method: "POST" },
  );
}

export function listInvitations(teamId: string): Promise<Invitation[]> {
  return fetchApiWithAuth<Invitation[]>(
    `/api/me/teams/${encodeURIComponent(teamId)}/invitations`,
  );
}

export function createInvitation(
  teamId: string,
  input: CreateInvitationInput,
): Promise<InvitationCreated> {
  return fetchApiWithAuth<InvitationCreated>(
    `/api/me/teams/${encodeURIComponent(teamId)}/invitations`,
    {
      method: "POST",
      body: input,
    },
  );
}

export function cancelInvitation(
  teamId: string,
  invitationId: string,
): Promise<InvitationCancelled> {
  return fetchApiWithAuth<InvitationCancelled>(
    `/api/me/teams/${encodeURIComponent(teamId)}/invitations/${encodeURIComponent(invitationId)}`,
    { method: "DELETE" },
  );
}

export function acceptInvitation(
  input: { code: string } | { token: string } | { invitationId: string },
): Promise<TeamJoined> {
  return fetchApiWithAuth<TeamJoined>("/api/me/teams/accept-invite", {
    method: "POST",
    body: input,
  });
}

export type PendingInvitation = {
  invitationId: string;
  teamId: string;
  teamName: string;
  role: string;
  expiresAt: string;
  createdAt: string;
  inviterName: string | null;
};

/** Incoming pending invites addressed to the signed-in phone (PF-372). */
export function listPendingInvitations(): Promise<PendingInvitation[]> {
  return fetchApiWithAuth<PendingInvitation[]>(
    "/api/me/teams/pending-invitations",
  );
}

export function declinePendingInvitation(
  invitationId: string,
): Promise<InvitationDeclined> {
  return fetchApiWithAuth<InvitationDeclined>("/api/me/teams/decline-invite", {
    method: "POST",
    body: { invitationId },
  });
}

// ── Public token surface (no session — the WhatsApp URL buttons land here) ──

export function lookupInvitationToken(
  token: string,
): Promise<InvitationTokenLookup> {
  return fetchApiPublic<InvitationTokenLookup>(
    `/api/public/team-invitations/${encodeURIComponent(token)}`,
  );
}

export function declineInvitationByToken(
  token: string,
): Promise<InvitationDeclined> {
  return fetchApiPublic<InvitationDeclined>(
    "/api/public/team-invitations/decline",
    { method: "POST", body: { token } },
  );
}

export function leaveTeam(
  teamId: string,
  userId: string,
): Promise<LeaveTeamResponse> {
  return fetchApiWithAuth<LeaveTeamResponse>(
    `/api/me/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}/leave`,
    { method: "POST" },
  );
}

export function updateMemberRole(teamId: string, userId: string, input: { role?: CreateInvitationInput["role"]; customRoleId?: string }) {
  return fetchApiWithAuth<{ userId: string; role: string }>(`/api/me/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}/role`, { method: "PATCH", body: input });
}
